import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [agreeNewsletter, setAgreeNewsletter] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreeNewsletter || !email) return;

    setIsSubmitting(true);
    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d68t7ddt1k61917clv7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setAgreeNewsletter(false);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-50 pt-32">
      {/* Top Bar */}
      <div className="border-t border-slate-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col xl:flex-row xl:items-stretch">
            {/* Logo */}
            <div className="w-full xl:w-[35%] flex items-center justify-start py-8 xl:py-[70px] xl:pr-8">
              <img
                src="https://static.readdy.ai/image/9b819ec02f0cc35e99e8e09b39e5fe1e/4299ef1f9e55f3d4ef76bc3b6e5af17b.png"
                alt="CJ Solutions Logo"
                width={180}
                height={48}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto"
              />
            </div>

            <div className="xl:hidden w-full h-px bg-slate-900/10"></div>

            {/* Address */}
            <div className="w-full xl:w-[40%] flex items-center justify-start xl:border-l xl:border-slate-900/10 py-8 xl:py-[70px]">
              <div className="xl:px-8">
                <a
                  href="https://maps.google.com/?q=Limassol,+Cyprus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                >
                  <i className="ri-map-pin-line text-amber-500"></i>
                  <span>Limassol, Cyprus</span>
                </a>
              </div>
            </div>

            <div className="xl:hidden w-full h-px bg-slate-900/10"></div>

            {/* Phone */}
            <div className="w-full xl:w-[25%] flex items-center justify-start py-8 xl:py-[70px] xl:border-l xl:border-slate-900/10">
              <div className="xl:px-8">
                <a
                  href="tel:+35795981879"
                  className="flex items-center gap-2 text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                >
                  <i className="ri-phone-line text-amber-500"></i>
                  <span>+357 95981879</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="border-t border-slate-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col xl:flex-row xl:items-stretch">
          {/* Newsletter */}
          <div className="w-full xl:w-[25%] py-12 xl:py-[70px] xl:border-r xl:border-slate-900/10 xl:pr-16">
            <div className="mb-12">
              <h3 className="text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight mb-4">
                Get inspired for your next project!
              </h3>
              <p className="text-xl text-slate-900 mb-6">
                Subscribe to our newsletter now!
              </p>
              <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-4">
                <div className="flex">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter Your Email Address"
                    required
                    className="flex-1 px-4 py-3 border border-slate-300 rounded-l-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting || !agreeNewsletter}
                    className="px-4 py-3 bg-amber-500 text-white rounded-r-lg hover:bg-amber-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="newsletter-privacy"
                    checked={agreeNewsletter}
                    onChange={(e) => setAgreeNewsletter(e.target.checked)}
                    className="mt-1 w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500 cursor-pointer"
                  />
                  <label htmlFor="newsletter-privacy" className="text-sm text-slate-600 cursor-pointer">
                    I agree to the <Link to="/privacy-policy" className="text-amber-500 underline">privacy policy</Link>
                  </label>
                </div>
                {submitStatus === 'success' && (
                  <p className="text-green-600 text-sm">Thank you for subscribing!</p>
                )}
              </form>
            </div>

            <div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">We're on Socials:</h4>
              <div className="flex gap-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61563327389236"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-lg border border-slate-900/10 flex items-center justify-center hover:bg-slate-100 transition-colors"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-slate-900 text-xl"></i>
                </a>
                <a
                  href="https://www.linkedin.com/company/cjsolutionsltd/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-lg border border-slate-900/10 flex items-center justify-center hover:bg-slate-100 transition-colors"
                  aria-label="LinkedIn"
                >
                  <i className="ri-linkedin-fill text-slate-900 text-xl"></i>
                </a>
                <a
                  href="https://x.com/CJSolutionsltd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-lg border border-slate-900/10 flex items-center justify-center hover:bg-slate-100 transition-colors"
                  aria-label="Twitter"
                >
                  <i className="ri-twitter-x-fill text-slate-900 text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="w-full xl:flex-1 flex flex-col xl:flex-row py-8 xl:py-[70px] xl:pl-16">
            <div className="flex-1 mb-8 xl:mb-0">
              <h4 className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-6">
                Useful Links
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/about"
                    className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                  >
                    Company Overview
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/resources" className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors">
                    Resources
                  </Link>
                </li>
                <li>
                  <Link to="/terms-and-conditions" className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors">
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            <div className="flex-1">
              <div className="xl:hidden w-full h-px bg-slate-900/10 mb-8"></div>
              <h4 className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-6">
                Services
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/services/igaming-fx-licensing"
                    className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                  >
                    iGaming & FX Licensing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/aml-kyc-outsourcing"
                    className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                  >
                    AML & KYC Outsourcing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/compliance-outsourcing"
                    className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                  >
                    Compliance Outsourcing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/tax-payroll-outsourcing"
                    className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                  >
                    Tax & Payroll Outsourcing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/accounting-outsourcing"
                    className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                  >
                    Accounting Outsourcing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/legal-drafting-outsourcing"
                    className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                  >
                    Legal Drafting
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/company-formation"
                    className="text-slate-900 text-lg font-medium hover:text-amber-500 transition-colors"
                  >
                    Company Formation
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4 text-base">
            <p className="text-slate-900/70">
              © 2025, <span className="text-slate-900 font-semibold underline">CJ Solutions Ltd.</span> All Rights Reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/terms-and-conditions" className="text-slate-900/70 hover:text-amber-500 transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy-policy" className="text-slate-900/70 hover:text-amber-500 transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}